import React, { useState, useMemo } from "react";
import {
  Popover,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Divider,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LayersIcon from "@mui/icons-material/Layers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { Layer } from "../../models/maps/layer";

interface MapLayerControlProps {
  layers: Layer[];
  onToggleLayer: (layer: Layer) => void;
  onToggleGroup: (layers: Layer[], checked: boolean) => void;
}

const sortByDisplayName = (a: Layer, b: Layer) =>
  a.layerDisplayName.localeCompare(b.layerDisplayName, 'nl', { sensitivity: 'base' });

const MapLayerControl: React.FC<MapLayerControlProps> = ({
  layers,
  onToggleLayer,
  onToggleGroup
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [search, setSearch] = useState("");

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredLayers = useMemo(() => {
    if (!search.trim()) return layers;

    const lower = search.toLowerCase();

    return layers.filter(layer =>
      layer.layerDisplayName?.toLowerCase().includes(lower) ||
      layer.description?.toLowerCase().includes(lower) ||
      layer.sourceName?.toLowerCase().includes(lower) ||
      layer.mainTheme?.toLowerCase().includes(lower) ||
      layer.subTheme?.toLowerCase().includes(lower)
    );
  }, [search, layers]);

  const generalLayers = useMemo(
    () => filteredLayers.filter(layer => layer.type !== 'geopackage'),
    [filteredLayers]
  );

  // Kaartlagen zonder hoofdthema staan los onder Algemeen, dus zonder groepering.
  const ungroupedGeneralLayers = useMemo(
    () => generalLayers
      .filter(layer => !layer.mainTheme?.trim())
      .sort(sortByDisplayName),
    [generalLayers]
  );

  const themeGroupedLayers = useMemo(() => {
    const groups: Record<string, Layer[]> = {};

    generalLayers.forEach(layer => {
      const key = layer.mainTheme?.trim();

      if (!key) {
        return;
      }

      if (!groups[key]) groups[key] = [];
      groups[key].push(layer);
    });

    return Object.entries(groups)
      .map(([mainTheme, themeLayers]) => {
        const withoutSubTheme: Layer[] = [];
        const subThemes: Record<string, Layer[]> = {};

        themeLayers.forEach(layer => {
          const subTheme = layer.subTheme?.trim();

          if (!subTheme) {
            withoutSubTheme.push(layer);
            return;
          }

          if (!subThemes[subTheme]) subThemes[subTheme] = [];
          subThemes[subTheme].push(layer);
        });

        return {
          mainTheme,
          layers: themeLayers,
          withoutSubTheme: withoutSubTheme.sort(sortByDisplayName),
          subThemes: Object.entries(subThemes)
            .map(([subTheme, subThemeLayers]) => ({
              subTheme,
              layers: [...subThemeLayers].sort(sortByDisplayName)
            }))
            .sort((a, b) => a.subTheme.localeCompare(b.subTheme, 'nl', { sensitivity: 'base' }))
        };
      })
      .sort((a, b) => a.mainTheme.localeCompare(b.mainTheme, 'nl', { sensitivity: 'base' }));
  }, [generalLayers]);

  const assetGroupedLayers = useMemo(() => {
    const groups: Record<string, Layer[]> = {};

    filteredLayers.forEach(layer => {
      if (layer.type !== 'geopackage') {
        return;
      }

      const key = layer.sourceName || "Overig";

      if (!groups[key]) groups[key] = [];
      groups[key].push(layer);
    });

    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) =>
        a.layerDisplayName.localeCompare(b.layerDisplayName)
      );
    });

    return Object.entries(groups).sort(([a], [b]) =>
      a.localeCompare(b)
    );
  }, [filteredLayers]);

  const renderLayerRow = (layer: Layer) => (
    <Box
      key={layer.id}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        pl: 0
      }}
    >
      <FormControlLabel
        sx={{
          marginLeft: 0,
          marginRight: 0,
        }}
        control={
          <Checkbox
            checked={layer.selected ?? false}
            onChange={() => onToggleLayer(layer)}
            size="small"
          />
        }
        label={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}
          >
            <span>{layer.layerDisplayName}</span>
          </Box>
        }
      />

      {(layer.description || layer.metadataUrl) && (
        <Tooltip
          title={(
            <Box>
              {layer.description && (
                <Typography variant="body2" component="div">
                  {layer.description}
                </Typography>
              )}
              {layer.metadataUrl && (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    mt: layer.description ? 1 : 0,
                    '& a, & a:link, & a:visited, & a:hover, & a:active': {
                      color: '#fff'
                    }
                  }}
                >
                  Metadata:{' '}
                  <a
                    href={layer.metadataUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {layer.metadataUrl}
                  </a>
                </Typography>
              )}
            </Box>
          )}
          placement="right"
          arrow
        >
          <IconButton size="small">
            <InfoOutlinedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderGroupAccordion = (
    title: string,
    groupLayers: Layer[],
    sectionKey: string,
    children: React.ReactNode,
    options?: { showBottomBorder?: boolean }
  ) => {
    const allSelected = groupLayers.every(l => l.selected);
    const someSelected = groupLayers.some(l => l.selected) && !allSelected;
    const showBottomBorder = options?.showBottomBorder ?? true;

    return (
      <Accordion
        key={`${sectionKey}-${title}`}
        disableGutters
        sx={{
          boxShadow: "none",
          borderRadius: 0,
          "&.MuiAccordion-rounded": {
            borderRadius: 0,
            "&:first-of-type": {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
            "&:last-of-type": {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          },
          borderBottom: showBottomBorder ? "1px solid rgba(0,0,0,0.08)" : "none",
          "&:before": { display: "none" }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: "32px",
            paddingLeft: 0,
            paddingRight: 0,
            "& .MuiAccordionSummary-content": {
              margin: "4px 0"
            },
            // Zelfde breedte als de info-IconButton zodat de iconen rechts uitlijnen.
            "& .MuiAccordionSummary-expandIconWrapper": {
              width: "28px",
              display: "flex",
              justifyContent: "center"
            }
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginLeft: 0,
            }}>
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                onToggleGroup(groupLayers, e.target.checked)
              }
              size="small"
            />

            <Typography fontWeight={600}>
              {title}
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            pt: 0,
            pb: 0.5,
            pl: 2,
            pr: 0
          }}
        >
          {children}
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderSourceAccordion = (sourceName: string, groupedSourceLayers: Layer[], sectionKey: string) =>
    renderGroupAccordion(
      sourceName,
      groupedSourceLayers,
      sectionKey,
      groupedSourceLayers.map(renderLayerRow)
    );

  const renderThemeAccordion = (group: typeof themeGroupedLayers[number]) =>
    renderGroupAccordion(
      group.mainTheme,
      group.layers,
      "wms",
      <>
        {group.withoutSubTheme.map(renderLayerRow)}

        {!!group.withoutSubTheme.length && !!group.subThemes.length && (
          <Divider sx={{ my: 0.5, borderColor: "rgba(0,0,0,0.08)" }} />
        )}

        {group.subThemes.map(({ subTheme, layers: subThemeLayers }, index) =>
          renderGroupAccordion(
            subTheme,
            subThemeLayers,
            `wms-${group.mainTheme}`,
            subThemeLayers.map(renderLayerRow),
            { showBottomBorder: index !== group.subThemes.length - 1 }
          )
        )}
      </>
    );

  return (
    <>
      {!!layers.length && (
        <button
          type="button"
          className="azure-map-button azure-map-button-top-left-second"
          onClick={handleClick}
        >
          <LayersIcon fontSize="small" />
        </button>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left"
        }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              width: "min(92vw, 550px)",
              maxWidth: "550px",
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: "10px",
            }
          }
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} mb={1}>
          Kaartlagen
        </Typography>

        <TextField
          fullWidth
          variant="filled"
          size="small"
          hiddenLabel
          placeholder="Zoek kaartlaag..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            mb: 1,
            "& .MuiFilledInput-root": {
              borderRadius: "4px",
              backgroundColor: "#f5f5f5"
            }
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="wis zoekfilter"
                    onClick={() => setSearch("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined
            }
          }}
        />

        <Typography variant="caption" color="text.secondary">
          {search.trim().length > 0
            ? `${filteredLayers.length} van ${layers.length} kaartlagen`
            : filteredLayers.length === 1
              ? "1 kaartlaag"
              : `${filteredLayers.length} kaartlagen`}
        </Typography>

        <Divider sx={{ mb: 1, borderRadius: 0 }} />

        <Box sx={{ maxHeight: 500, overflowY: "auto", pr: "15px" }}>
          {(!!ungroupedGeneralLayers.length || !!themeGroupedLayers.length) && (
            <Box sx={{ mb: assetGroupedLayers.length ? 1 : 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                Algemeen
              </Typography>

              {!!ungroupedGeneralLayers.length && (
                <Box sx={{ pb: 0.5, borderRadius: 0 }}>
                  {ungroupedGeneralLayers.map(renderLayerRow)}
                </Box>
              )}

              {!!ungroupedGeneralLayers.length && !!themeGroupedLayers.length && (
                <Divider sx={{ mb: 0.5, borderRadius: 0, borderColor: "rgba(0,0,0,0.08)" }} />
              )}

              {themeGroupedLayers.map(renderThemeAccordion)}
            </Box>
          )}

          {!!assetGroupedLayers.length && (
            <Box>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
                Organisaties
              </Typography>
              {assetGroupedLayers.map(([sourceName, sourceLayers]) =>
                renderSourceAccordion(sourceName, sourceLayers, "asset")
              )}
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default MapLayerControl;